import { LatLng, LatLngBounds } from "leaflet";
import L from "leaflet";
import proj4 from "proj4";

import { ITileCoordinates } from "./tileCoordinates";


const TILE_SIZE_PX = 256;

export interface ICrsZoomMapEntry {
    crsCode: string;
    zoomLevels: number[];
}

export interface ICrsResolutionMapEntry {
    crsCode: string;
    resolutions: number[];
    zoomTranslationMap: ICrsZoomMapEntry[]
}

export const crsResolutionMap: ICrsResolutionMapEntry[] = [
    { 
        crsCode: "EPSG:27700", 
        resolutions: [ 896.0, 448.0, 224.0, 112.0, 56.0, 28.0, 14.0, 7.0, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375 ],
        zoomTranslationMap: [
            { crsCode: "EPSG:3857", zoomLevels: [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] }
        ]
    },
    { 
        crsCode: "EPSG:3857", 
        resolutions: [ NaN, NaN, NaN, NaN, NaN, NaN, NaN, 1,223, 611, 306, 153, 76, 38, 19, 9.6, 4.8, 2.4, 1.2, 0.6, 0.3, 0.15 ],
        zoomTranslationMap: [
            { crsCode: "EPSG:27700", zoomLevels: [0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13] }
        ]
    }
];

export const crsPresets = {
    //Setup the EPSG:27700 (British National Grid) projection
    OS7700: new L.Proj.CRS('EPSG:27700', '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs', {
        resolutions: crsResolutionMap.find(me => me.crsCode === "EPSG:27700")?.resolutions,
        origin: [ -238375.0, 1376256.0 ]
    }),

    //Web Mercator Projection
    WebMercator: L.CRS.EPSG3857
}

/**
 * 
 * @param value [X, Y]
 * @returns L.LatLng
 */
export const coordsBgsToLatLng = function(arr: [number, number]): LatLng {
    const lngLat = proj4('EPSG:27700', 'EPSG:4326', arr);
    return L.latLng(lngLat[1], lngLat[0]);
};

/**
 * 
 * @param value L.LatLng
 * @returns [X,Y]
 */
export const coordsLatLngToBgs = function(value: LatLng): [number, number] {
    const xY =  proj4('EPSG:4326', 'EPSG:27700', [value.lng, value.lat]);
    return [Math.round(xY[0]), Math.round(xY[1])];
};

/**
 * 
 * @param value L.LatLngBounds
 * @returns [XMIN,YMIN,XMAX,YMAX]
 */
export const boundsLatLngToBgs = function(value: LatLngBounds): [number, number, number, number] {

    const minLat = Math.min(value.getNorth(), value.getSouth());
    const minLng = Math.min(value.getWest(), value.getEast());
    const maxLat = Math.max(value.getNorth(), value.getSouth());
    const maxLng = Math.max(value.getWest(), value.getEast());

    const minBgs = proj4('EPSG:4326', 'EPSG:27700', [minLng, minLat]);
    const maxBgs = proj4('EPSG:4326', 'EPSG:27700', [maxLng, maxLat]);

    return [
        Math.round(minBgs[0]), Math.round(minBgs[1]), 
        Math.round(maxBgs[0]), Math.round(maxBgs[1])
    ];

};

/**
 * 
 * @param value [XMIN,YMIN,XMAX,YMAX]
 * @returns L.LatLngBounds
 */
export const boundsBgsToLatLng = function(value: [number, number, number, number]): LatLngBounds {

    const minLatLng = proj4('EPSG:27700', 'EPSG:4326', [value[0], value[1]]);
    const maxLatLng = proj4('EPSG:27700', 'EPSG:4326', [value[2], value[3]]);

    const sw = L.latLng(minLatLng[1], minLatLng[0]);  //Min
    const ne = L.latLng(maxLatLng[1], maxLatLng[0]);  //Max

    return L.latLngBounds(sw, ne);
};

export const getTilesWithinBounds = (crs: L.CRS, bounds: L.LatLngBounds, maxZ: number, minZ: number = 0): ITileCoordinates[] => {
    const results: ITileCoordinates[] = [];

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    for (let z = minZ; z <= maxZ; z += 1) {

        const p1 = crs.latLngToPoint(sw, z);
        const p2 = crs.latLngToPoint(ne, z);

        var minX = Math.floor(Math.min(p1.x, p2.x) / TILE_SIZE_PX);
        var maxX = Math.floor(Math.max(p1.x, p2.x) / TILE_SIZE_PX);
        var minY = Math.floor(Math.min(p1.y, p2.y) / TILE_SIZE_PX);
        var maxY = Math.floor(Math.max(p1.y, p2.y) / TILE_SIZE_PX);

        for (let x = minX; x <= maxX; x += 1) {
            for (let y = minY; y <= maxY; y += 1) {
                results.push({z, x, y});
            }
        }

    }

    return results;
};

export const getClosestZoomLevel = (oldCrsCode: string, newCrsCode: string, oldZoom: number): number => {

    if (oldCrsCode === newCrsCode)
        return oldZoom;

    //Refactor
    const oldCrsTransMap = crsResolutionMap.find(me => me.crsCode === oldCrsCode)?.zoomTranslationMap;

    if (!oldCrsTransMap)
        return oldZoom;

    const zoomMap = oldCrsTransMap.find(me => me.crsCode === newCrsCode);
    if (!zoomMap)
        return oldZoom;

    const newZoom = zoomMap.zoomLevels[oldZoom] ?? oldZoom;

    return newZoom
};

const setMapCRSAndZoomLimitsInternal = (map: L.Map, crs: L.CRS, minZoom: number, maxZoom: number, oldZoom: number, newZoom: number) => {

    const centre  = map.getCenter();

    map.options.crs = crs;
    map.options.minZoom = minZoom;
    map.options.maxZoom = maxZoom;

    map.options.zoomSnap = 0;   // Ensure zoom is not affected by differing CRS scales

    map.setView(centre, newZoom, { animate: false }); //we need this, because after changing crs the centre is shifted

    map.options.zoomSnap = 1;   // Ensure zoom is not affected by differing CRS scales

};

export const changeMapCRSAndZoomLimits = (map: L.Map, newCrs: L.CRS, minZoom: number, maxZoom: number) => {

    const oldZoom = Math.ceil(map.getZoom());

    if (map.options.crs?.code === newCrs.code && map.options.minZoom === minZoom && map.options.maxZoom === maxZoom) {
        return oldZoom;
    }

    const closestZoom = getClosestZoomLevel(map.options.crs!.code!, newCrs.code!, oldZoom);

    const newZoom = Math.min(Math.max(closestZoom, minZoom), maxZoom);
 
    setMapCRSAndZoomLimitsInternal(map, newCrs, minZoom, maxZoom, oldZoom, newZoom);
};

export const setInitialCRSAndZoomLimits = (map: L.Map, newCrs: L.CRS, minZoom: number, maxZoom: number, initialZoom: number) => {

    const oldZoom = Math.ceil(map.getZoom());
    const newZoom = Math.min(Math.max(initialZoom, minZoom), maxZoom);

    setMapCRSAndZoomLimitsInternal(map, newCrs, minZoom, maxZoom, oldZoom, newZoom);

};

/**
 * 
 * @param data L.LatLn
 * @returns [lng, lat]
 */
export const pointToArray = (data: L.LatLng): [number, number] => {
    const latLng = data;
    return [latLng.lng, latLng.lat];
}

/**
 * 
 * @param data [lng, lat]
 * @returns L.LatLng
 */
export const arrayToPoint = (data: [number, number]): L.LatLng => {
    return L.latLng(data[1], data[0]);
}

/**
 * 
 * @param data L.LatLngBounds
 * @returns [neLng, neLat, swLng, swLat]
 */
export const boundsToArray = (data: L.LatLngBounds): [number, number, number, number] => {
    const ne = data.getNorthEast();
    const sw = data.getSouthWest();
    return [ne.lat, ne.lng, sw.lat, sw.lng];
}

/**
 * 
 * @param data [swLng, swLat, neLng, neLat]
 * @returns L.LatLngBounds
 */
export const arrayToBounds = (data: [number, number, number, number]): L.LatLngBounds => {
    const sw = L.latLng(data[0], data[1]);  //Min
    const ne = L.latLng(data[2], data[3]);  //Max
    return L.latLngBounds(sw, ne);
}


const osGridPrefixes = [
    [ 'SV', 'SW', 'SX', 'SY', 'SZ', 'TV', 'TW' ],
    [ 'SQ', 'SR', 'SS', 'ST', 'SU', 'TQ', 'TR' ],
    [ 'SL', 'SM', 'SN', 'SO', 'SP', 'TL', 'TM' ],
    [ 'SF', 'SG', 'SH', 'SJ', 'SK', 'TF', 'TG' ],
    [ 'SA', 'SB', 'SC', 'SD', 'SE', 'TA', 'TB' ],
    [ 'NV', 'NW', 'NX', 'NY', 'NZ', 'OV', 'OW' ],
    [ 'NQ', 'NR', 'NS', 'NT', 'NU', 'OQ', 'OR' ],
    [ 'NL', 'NM', 'NN', 'NO', 'NP', 'OL', 'OM' ],
    [ 'NF', 'NG', 'NH', 'NJ', 'NK', 'OF', 'OG' ],
    [ 'NA', 'NB', 'NC', 'ND', 'NE', 'OA', 'OB' ],
    [ 'HV', 'HW', 'HX', 'HY', 'HZ', 'JV', 'JW' ],
    [ 'HQ', 'HR', 'HS', 'HT', 'HU', 'JQ', 'JR' ],
    [ 'HL', 'HM', 'HN', 'HO', 'HP', 'JL', 'JM' ]
];

export interface IOsGridRef {
    prefix: string;
    ea: string;
    no: string;
}

/**
 * 
 * @param data [X,Y]
 * @returns IOsGridRef
 */
export const toOSGridRef = (data: [number, number]): IOsGridRef | null => {

    const ea = data[0];
    const no = data[1];

    const x = Math.floor(ea / 100000);
    const y = Math.floor(no / 100000);

    const prefixRow = osGridPrefixes[y];
    if (!prefixRow)
        return null;

    const prefix = prefixRow[x];
    if (!prefix)
        return null;

    const e = Math.floor(ea % 100000);
    const n = Math.floor(no % 100000);

    const strE = String(e).padStart(5, '0');
    const strN = String(n).padStart(5, '0');

    return { prefix, ea: strE, no: strN };
}